/*
 * Hanging lantern — small static post + an animated warm-glow ring that flickers.
 *
 * `update(t, intensity)` accepts an optional intensity multiplier (default 1)
 * so the day/night cycle (TASK-10) can brighten lanterns at deep twilight and
 * dim them at dawn. game.ts passes `lanternIntensityFor(worldState)` each frame.
 * The multiplier scales both halo alphas and the window-pane alpha; the flicker
 * recipe itself (the breathing) is preserved exactly per AESTHETIC.md §6.3.
 */

import { Container, Graphics } from 'pixi.js';

export interface LanternView {
  container: Container;
  /**
   * Per-frame update.
   * @param t time in ms used by the flicker sine
   * @param intensity multiplier on the glow output. Default 1 (v0.2 baseline).
   *   The cozy clamp: we soft-cap inside so a runaway multiplier can't bloom
   *   past alpha 1 and the dim case never goes fully dark (lanterns are *lit*
   *   in this world — they just breathe).
   */
  update(t: number, intensity?: number): void;
}

const MIN_INTENSITY = 0.5;
const MAX_INTENSITY = 1.8;

export function createLantern(x: number, y: number): LanternView {
  const root = new Container();
  root.position.set(x, y);

  const glow = new Graphics();
  root.addChild(glow);

  // Static structure: hook + post + frame + window.
  const post = new Graphics();
  post.rect(-1.2, 2, 2.4, 22).fill(0x3a2410);
  post
    .moveTo(0, 2)
    .quadraticCurveTo(5, 0, 0, -6)
    .stroke({ color: 0x3a2410, width: 1.5 });
  post.roundRect(-5, -7, 10, 9, 1.5).fill(0x3a2410);
  root.addChild(post);

  const window = new Graphics();
  root.addChild(window);

  // Seed phase from x so neighbouring lanterns don't synchronize.
  const phaseSeed = x;

  function update(t: number, intensity: number = 1): void {
    const k = Math.max(MIN_INTENSITY, Math.min(MAX_INTENSITY, intensity));
    const flicker = 0.85 + Math.sin(t * 0.005 + phaseSeed) * 0.1;
    const lit = flicker * k;

    glow.clear();
    glow.circle(0, -2, 35).fill({ color: 0xffc56b, alpha: Math.min(1, 0.18 * lit) });
    glow.circle(0, -2, 22).fill({ color: 0xffc56b, alpha: Math.min(1, 0.25 * lit) });

    window.clear();
    window.roundRect(-3.5, -5.5, 7, 6, 1).fill({ color: 0xffc56b, alpha: Math.min(1, lit) });
  }

  return { container: root, update };
}
